import type { ActivityDto, AlertDto } from '@pos/api-client';
import { formatAmount, formatQuantity, pluralRu } from '@pos/business-logic';
import { ErrorState, ListGroup, ListRow, Money, Skeleton, cn } from '@pos/ui';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Bell, Boxes, ChevronRight, LockOpen, Lock, PackagePlus, ReceiptText, RefreshCw, Undo2, WifiOff } from 'lucide-react';
import { useNavigate } from 'react-router';
import { HeaderButton, Screen } from '../../app/Screen';
import { BranchChip } from '../../app/BranchChip';
import { useDashboard, useNotifications } from '../../app/data';
import { METHOD_LABEL, ago, todayLong } from '../../lib/format';

function Card({ children, className, onClick }: { children: React.ReactNode; className?: string; onClick?: () => void }) {
  const cls = cn('rounded-2xl border border-line bg-surface p-4 text-left', className);
  return onClick ? (
    <button type="button" onClick={onClick} className={cn(cls, 'w-full active:bg-sunken')}>
      {children}
    </button>
  ) : (
    <div className={cls}>{children}</div>
  );
}

function Delta({ now, before }: { now: number; before: number }) {
  if (before <= 0) return <span className="text-ink-3">Вчера к этому часу продаж не было</span>;
  const pct = Math.round(((now - before) / before) * 100);
  const up = pct >= 0;
  return (
    <span className={cn('inline-flex items-center gap-1 font-medium', up ? 'text-success' : 'text-danger')}>
      {up ? <ArrowUpRight className="size-4" /> : <ArrowDownLeft className="size-4" />}
      {up ? '+' : ''}
      {pct}% <span className="font-normal text-ink-3">к вчера на это время</span>
    </span>
  );
}

const ALERT_ICON: Record<AlertDto['kind'], React.ReactNode> = {
  DEVICE_STALE: <WifiOff className="size-5" />,
  CASH_DIFFERENCE: <AlertTriangle className="size-5" />,
  SYNC_REJECTED: <AlertTriangle className="size-5" />,
  NEGATIVE_STOCK: <Boxes className="size-5" />,
  LOW_STOCK: <Boxes className="size-5" />,
};

const ACTIVITY_ICON: Record<ActivityDto['kind'], React.ReactNode> = {
  SALE: <ReceiptText className="size-5 text-primary" />,
  RETURN: <Undo2 className="size-5 text-warning" />,
  PURCHASE: <PackagePlus className="size-5 text-success" />,
  SHIFT_OPEN: <LockOpen className="size-5 text-ink-3" />,
  SHIFT_CLOSE: <Lock className="size-5 text-ink-3" />,
  STOCK_DOC: <Boxes className="size-5 text-ink-3" />,
};

function Hours({ values }: { values: number[] }) {
  const first = Math.min(8, ...values.map((v, h) => (v > 0 ? h : 24)));
  const last = Math.max(21, ...values.map((v, h) => (v > 0 ? h : 0)));
  const shown = values.slice(first, last + 1);
  const peak = Math.max(...shown, 1);
  const nowHour = new Date().getHours();
  return (
    <div className="flex h-28 items-end gap-[3px]" role="img" aria-label="Выручка по часам">
      {shown.map((v, i) => {
        const h = first + i;
        return (
          <div key={h} className="flex h-full flex-1 flex-col justify-end gap-1">
            <div
              className={cn('w-full rounded-t-[3px]', h === nowHour ? 'bg-primary' : 'bg-primary/35')}
              style={{ height: `${v ? Math.max(6, (v / peak) * 100) : 2}%` }}
            />
            <span className="text-center text-2xs leading-none text-ink-3 tnum">{h % 3 === 0 ? h : ''}</span>
          </div>
        );
      })}
    </div>
  );
}

export function HomePage() {
  const { data: d, isPending, isError, refetch, isFetching } = useDashboard();
  const navigate = useNavigate();
  const unread = useNotifications().data?.unread ?? 0;

  return (
    <Screen
      title="Обзор"
      actions={
        <>
          <HeaderButton label={unread > 0 ? `Уведомления: ${unread} новых` : 'Уведомления'} onClick={() => navigate('/more/notifications')} className="relative">
            <Bell />
            {unread > 0 && (
              <span className="absolute top-1 right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger px-1 text-2xs font-bold text-white tnum">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </HeaderButton>
          <HeaderButton label="Обновить" onClick={() => void refetch()}>
            <RefreshCw className={cn(isFetching && 'animate-spin')} />
          </HeaderButton>
        </>
      }
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-md text-ink-3">{todayLong()}</p>
        <BranchChip />
      </div>

      {isError && !d ? (
        <ErrorState description="Не удалось загрузить сводку." onRetry={() => void refetch()} />
      ) : isPending || !d ? (
        <div className="space-y-3">
          <Skeleton className="h-36 rounded-lg" />
          <div className="grid grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {d.alerts.length > 0 && (
            <ul className="space-y-2" aria-label="Требует внимания">
              {d.alerts.map((a, i) => (
                <li
                  key={`${a.kind}-${i}`}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border px-4 py-3',
                    a.severity === 'danger' ? 'border-danger-line bg-danger-soft text-danger' : 'border-warning-line bg-warning-soft text-warning',
                  )}
                >
                  <span className="mt-0.5 shrink-0">{ALERT_ICON[a.kind]}</span>
                  <div className="min-w-0">
                    <p className="font-medium">{a.title}</p>
                    {a.detail && a.kind !== 'DEVICE_STALE' && <p className="text-sm opacity-80">{a.detail}</p>}
                    {a.kind === 'DEVICE_STALE' && <p className="text-sm opacity-80">{a.at ? `Последняя связь ${ago(a.at)}` : 'Ещё не выходила на связь'}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <Card onClick={() => navigate('/sales')}>
            <p className="text-sm text-ink-3">Выручка сегодня</p>
            <Money value={d.totals.revenue} className="mt-1 block text-4xl font-bold tracking-[-0.03em] min-[400px]:text-5xl" />
            <p className="mt-2 text-sm">
              <Delta now={d.totals.revenue} before={d.yesterdaySameTime.revenue} />
            </p>
            {d.totals.refunds > 0 && <p className="mt-1 text-sm text-ink-3 tnum">Возвраты: −{formatAmount(d.totals.refunds)} сум</p>}
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <Card>
              <p className="text-sm text-ink-3">Чеков</p>
              <p className="mt-1 text-2xl font-semibold tnum">{d.totals.count}</p>
            </Card>
            <Card>
              <p className="text-sm text-ink-3">Средний чек</p>
              <Money value={d.totals.average} className="mt-1 block text-xl font-semibold min-[400px]:text-2xl" />
            </Card>
            <Card>
              <p className="text-sm text-ink-3">В кассах</p>
              <Money value={d.cash.amount} className="mt-1 block text-xl font-semibold min-[400px]:text-2xl" />
              <p className="mt-0.5 text-xs text-ink-3">
                {d.cash.openShifts ? `${d.cash.openShifts} ${pluralRu(d.cash.openShifts, ['смена открыта', 'смены открыты', 'смен открыто'])}` : 'Смены закрыты'}
              </p>
            </Card>
            <Card onClick={() => navigate('/more/expenses')}>
              <p className="text-sm text-ink-3">Расходы</p>
              <Money value={d.totals.expenses} className="mt-1 block text-xl font-semibold min-[400px]:text-2xl" />
            </Card>
          </div>

          <Card>
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm text-ink-3">Прибыль, оценка</p>
              <Money value={d.totals.profit} className={cn('text-2xl font-semibold', d.totals.profit < 0 && 'text-danger')} />
            </div>
            <p className="mt-1 text-xs text-ink-3">
              Выручка − себестоимость {formatAmount(d.totals.cogs)} − расходы {formatAmount(d.totals.expenses)}
            </p>
          </Card>

          {d.totals.count > 0 && (
            <Card>
              <p className="mb-3 text-sm text-ink-3">По часам</p>
              <Hours values={d.byHour} />
            </Card>
          )}

          {d.payments.length > 0 && (
            <ListGroup title="Оплата" className="mb-0 pt-3">
              {d.payments.map((p) => (
                <ListRow key={p.method} title={METHOD_LABEL[p.method]} trailing={<Money value={p.amount} className="font-medium" />} />
              ))}
            </ListGroup>
          )}

          <ListGroup title={d.lowStock.count ? `Заканчиваются · ${d.lowStock.count}` : 'Заканчиваются'} className="mb-0 pt-3">
            {d.lowStock.items.length === 0 ? (
              <ListRow title={<span className="text-ink-3">Всего достаточно</span>} />
            ) : (
              <>
                {d.lowStock.items.map((i) => (
                  <ListRow
                    key={i.productId}
                    title={i.name}
                    subtitle={`Минимум ${formatQuantity(i.minStock, i.unit)}`}
                    trailing={<span className={cn('font-semibold tnum', i.quantity <= 0 ? 'text-danger' : 'text-warning')}>{i.quantity <= 0 ? 'нет' : formatQuantity(i.quantity, i.unit)}</span>}
                    onClick={() => navigate(`/products/${i.productId}`)}
                  />
                ))}
                <ListRow
                  title={<span className="font-medium text-primary">Оформить приход</span>}
                  leading={<PackagePlus className="size-5 text-primary" />}
                  trailing={<ChevronRight className="size-5 text-ink-4" />}
                  onClick={() => navigate('/stock/receive')}
                />
              </>
            )}
          </ListGroup>

          <ListGroup title="Последние события" className="pt-3">
            {d.activity.length === 0 ? (
              <ListRow title={<span className="text-ink-3">Событий пока нет</span>} />
            ) : (
              d.activity.map((a) => (
                <ListRow
                  key={`${a.kind}-${a.id}`}
                  leading={ACTIVITY_ICON[a.kind]}
                  title={a.title}
                  subtitle={[ago(a.at), a.detail].filter(Boolean).join(' · ')}
                  trailing={a.amount !== null ? <Money value={a.amount} className={cn('text-md font-medium', a.amount < 0 && 'text-danger')} /> : undefined}
                  onClick={a.kind === 'SALE' ? () => navigate(`/sales/${a.id}`) : undefined}
                />
              ))
            )}
          </ListGroup>
        </div>
      )}
    </Screen>
  );
}
